import axios from "axios";

const BASE = `${process.env.REACT_APP_BASE_URL}/nocApi/admin/email`;
const OPTS = { withCredentials: true };

const unwrap = (res, fallback) => {
  if (!res.data?.success) throw new Error(res.data?.message || fallback);
  return res.data.data;
};

export const getApiError = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

// ---------- Package context / contractor lookup ----------
// NEW backend contracts, not existing permit endpoints. All require admin access.
// Responses follow { success: true, data: ... }; failures include a message.
// Lookup endpoints search server-side, return a bounded array, and accept an
// optional AbortSignal so callers can cancel stale searches or detail requests.
const unwrapLookupList = (res, fallback) => {
  const data = unwrap(res, fallback);
  if (!Array.isArray(data)) throw new Error(fallback);
  return data;
};

// GET /packages?currentSession=...&search=...&limit=...
// data: [{ id: string, label: string, contractorId: string | null }]
// A non-empty search is required. It matches contractor name, owner name,
// property address, and package reference fields across all users' packages.
// Results are capped at 20 and labels do not expose package IDs.
// IDs must be stable, unique package identifiers, not table row indexes.
export const fetchEmailPackages = async (
  currentSession,
  { search = "", limit = 20, signal } = {},
) =>
  unwrapLookupList(
    await axios.get(`${BASE}/packages`, {
      ...OPTS,
      params: { currentSession, search: search.trim(), limit },
      signal,
    }),
    "Failed to load permit packages.",
  );

// GET /packages/:packageId?currentSession=...
// data: {
//   id: string,
//   label: string,
//   contractor: { id: string, name: string, email: string | null } | null,
//   placeholders: {
//     contractor_name: string | null,
//     company_name: string | null,
//     signing_link: string | null
//   }
// }
// The backend maps its package fields to these canonical placeholder keys.
// Missing values stay null/empty; never fabricate names or signing links.
// The contractor's primary package email prefills the compose form's CC field.
// Returned values support frontend warnings/preview only; the send endpoint
// must resolve trusted values again using packageId and enforce template scope.
export const fetchEmailPackage = async (
  currentSession,
  packageId,
  { signal } = {},
) => {
  const id = String(packageId ?? "").trim();
  if (!id) throw new Error("A permit package ID is required.");

  const data = unwrap(
    await axios.get(`${BASE}/packages/${encodeURIComponent(id)}`, {
      ...OPTS,
      params: { currentSession },
      signal,
    }),
    "Failed to load permit package details.",
  );
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("Failed to load permit package details.");
  }
  return data;
};

// GET /contractors?currentSession=...&search=...&limit=...
// data: [{ id: string, name: string, companyName: string | null }]
// Search should match contractor or company name; empty search returns an
// initial bounded list. IDs must match template.contractorId and package.contractor.id.
// Return contractors across all admins, not only those owned by the current admin.
export const fetchEmailContractors = async (
  currentSession,
  { search = "", limit = 50, signal } = {},
) =>
  unwrapLookupList(
    await axios.get(`${BASE}/contractors`, {
      ...OPTS,
      params: { currentSession, search: search.trim(), limit },
      signal,
    }),
    "Failed to load contractors.",
  );

// ---------- Templates ----------
export const fetchTemplates = async (currentSession) =>
  (await unwrap(
    await axios.get(`${BASE}/templates`, {
      ...OPTS,
      params: { currentSession },
    }),
    "Failed to load templates.",
  )) || [];

export const createTemplate = async (currentSession, payload) =>
  unwrap(
    await axios.post(`${BASE}/templates`, { ...payload, currentSession }, OPTS),
    "Failed to create template.",
  );

export const updateTemplate = async (currentSession, id, payload) =>
  unwrap(
    await axios.put(
      `${BASE}/templates/${id}`,
      { ...payload, currentSession },
      OPTS,
    ),
    "Failed to update template.",
  );

export const deleteTemplate = async (currentSession, id) =>
  unwrap(
    await axios.delete(`${BASE}/templates/${id}`, {
      ...OPTS,
      params: { currentSession },
    }),
    "Failed to delete template.",
  );

// ---------- Saved recipients ----------
export const fetchRecipients = async (currentSession) =>
  (await unwrap(
    await axios.get(`${BASE}/recipients`, {
      ...OPTS,
      params: { currentSession },
    }),
    "Failed to load saved recipients.",
  )) || [];

export const createRecipient = async (currentSession, payload) =>
  unwrap(
    await axios.post(
      `${BASE}/recipients`,
      { ...payload, currentSession },
      OPTS,
    ),
    "Failed to save recipient.",
  );

export const updateRecipient = async (currentSession, id, payload) =>
  unwrap(
    await axios.put(
      `${BASE}/recipients/${id}`,
      { ...payload, currentSession },
      OPTS,
    ),
    "Failed to update recipient.",
  );

export const deleteRecipient = async (currentSession, id) =>
  unwrap(
    await axios.delete(`${BASE}/recipients/${id}`, {
      ...OPTS,
      params: { currentSession },
    }),
    "Failed to delete recipient.",
  );

// recipients: [{ email, name? }] → returns Recipient[] (created, or existing if duplicate)
export const saveRecipientsBulk = async (currentSession, recipients) =>
  (await unwrap(
    await axios.post(
      `${BASE}/recipients/bulk`,
      { recipients, currentSession },
      OPTS,
    ),
    "Failed to save recipients.",
  )) || [];
